export const personalFinanceCourse = {
  id: "personal-finance-mastery-2024",
  title: "Personal Finance Mastery: Complete Life Financial Planning Course",
  shortDescription: "Master personal financial planning, investment strategies, retirement planning, and wealth building techniques for long-term financial success.",
  description: "Comprehensive personal finance course covering budgeting, investing, retirement planning, tax strategies, insurance, and estate planning for individuals and families.",
  category: "Finance",
  industryCategory: "Personal Finance",
  difficulty: "beginner",
  duration: 15,
  estimatedWeeks: 8,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from certified financial planners and wealth advisors",
  thumbnailUrl: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Create and manage personal budgets and financial plans",
    "Build emergency funds and manage debt effectively",
    "Understand investment options and build diversified portfolios",
    "Plan for retirement using 401(k)s, IRAs, and other vehicles",
    "Optimize tax strategies and minimize tax burden",
    "Choose appropriate insurance coverage for protection",
    "Understand estate planning and wealth transfer strategies",
    "Apply behavioral finance principles to improve financial decisions"
  ],

  prerequisites: [
    "Basic Math Skills",
    "No Finance Background Required"
  ],

  skills: [
    "Budgeting",
    "Investment Planning",
    "Retirement Planning",
    "Tax Planning",
    "Insurance Planning",
    "Estate Planning",
    "Debt Management",
    "Financial Goal Setting"
  ],

  marketData: {
    averageSalary: 65000,
    salaryRange: {
      min: 45000,
      max: 120000
    },
    jobDemand: 3.8,
    marketTrend: "stable",
    careerPaths: [
      "Financial Planner",
      "Wealth Advisor",
      "Investment Advisor",
      "Insurance Agent",
      "Tax Preparer",
      "Estate Planning Attorney"
    ],
    relatedSkills: [
      "CFP Certification",
      "Client Relations",
      "Financial Software",
      "Insurance Products",
      "Tax Law",
      "Investment Analysis"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "budgeting-basics",
      title: "Budgeting and Cash Flow Management",
      description: "Creating budgets, tracking expenses, and managing cash flow",
      order: 1,
      estimatedHours: 3.0,
      content: [
        {
          id: "budget-creation",
          title: "Creating Your Personal Budget",
          description: "Step-by-step guide to building and maintaining a personal budget",
          type: "finance-interactive",
          duration: 40,
          interactiveData: {
            exerciseType: "budget-builder",
            instructions: "Create a comprehensive personal budget by categorizing income and expenses, setting savings goals, and identifying areas for improvement.",
            scenario: {
              monthlyIncome: 5000,
              expenses: {
                housing: 1500,
                transportation: 600,
                food: 500,
                utilities: 200,
                entertainment: 300,
                other: 400
              },
              savingsGoals: {
                emergency: 500,
                retirement: 400,
                vacation: 200
              }
            },
            hints: [
              "Follow the 50/30/20 rule: 50% needs, 30% wants, 20% savings",
              "Track expenses for a month before creating your budget",
              "Build emergency fund before investing",
              "Review and adjust budget monthly"
            ]
          }
        },
        {
          id: "cashflow-management",
          title: "Cash Flow Management and Financial Habits",
          description: "Managing money flow and building healthy financial habits",
          type: "text",
          duration: 45,
          textContent: `# Cash Flow Management and Financial Habits

## Understanding Cash Flow

### Cash Flow Fundamentals

**Cash Inflows**
- **Primary Income**: Salary, wages, business income
- **Secondary Income**: Side hustles, freelance work, rental income
- **Passive Income**: Dividends, interest, royalties
- **One-time Income**: Bonuses, tax refunds, gifts, inheritance

**Cash Outflows**
- **Fixed Expenses**: Rent/mortgage, insurance, loan payments
- **Variable Expenses**: Groceries, utilities, gas, entertainment
- **Periodic Expenses**: Annual insurance, taxes, maintenance
- **Discretionary Spending**: Dining out, hobbies, luxury items

**Net Cash Flow Calculation**
Net Cash Flow = Total Inflows - Total Outflows
- Positive: Building wealth and financial stability
- Negative: Depleting savings or increasing debt
- Break-even: Living paycheck to paycheck

### Cash Flow Optimization Strategies

**Increasing Income**
- **Career Development**: Skills training, certifications, networking
- **Side Hustles**: Freelancing, consulting, gig economy work
- **Passive Income Streams**: Dividend investing, rental properties
- **Negotiation**: Salary increases, better job offers

**Reducing Expenses**
- **Fixed Cost Reduction**: Refinancing, subscription audits
- **Variable Cost Management**: Budgeting, spending tracking
- **Smart Shopping**: Coupons, bulk buying, comparison shopping
- **Lifestyle Optimization**: Housing downsizing, transportation alternatives

## Budgeting Methods and Systems

### Zero-Based Budgeting

**Concept**: Every dollar has a specific purpose
- Assign all income to expense categories or savings
- Income minus expenses equals zero
- Forces intentional spending decisions
- Prevents lifestyle inflation

**Implementation Steps**:
1. Calculate total monthly income
2. List all fixed expenses
3. Estimate variable expenses
4. Allocate remaining funds to savings/debt payment
5. Adjust until income minus expenses equals zero

### 50/30/20 Budget Rule

**Allocation Framework**:
- **50% Needs**: Essential expenses (housing, food, utilities, minimum debt payments)
- **30% Wants**: Discretionary spending (entertainment, dining out, hobbies)
- **20% Savings and Debt**: Emergency fund, retirement, extra debt payments

**Benefits**:
- Simple and easy to implement
- Balanced approach to spending and saving
- Flexible within each category
- Good starting point for beginners

**Limitations**:
- May not work for very low or high incomes
- Categories may need adjustment based on circumstances
- Doesn't account for irregular expenses

### Envelope Budgeting

**Traditional Method**:
- Allocate cash to physical envelopes for each spending category
- When envelope is empty, no more spending in that category
- Forces spending discipline and awareness
- Prevents overspending

**Digital Envelope Systems**:
- Apps like YNAB (You Need A Budget), EveryDollar
- Virtual envelopes for different spending categories
- Real-time tracking and alerts
- Automatic categorization features

### Pay Yourself First

**Principle**: Save and invest before spending on discretionary items
- Automatic transfers to savings on payday
- Treats savings as a non-negotiable expense
- Builds wealth consistently over time
- Reduces temptation to overspend

**Implementation**:
1. Set up automatic transfers to savings accounts
2. Contribute to retirement accounts before receiving paycheck
3. Live on remaining income after savings
4. Increase savings rate with income growth

## Expense Tracking and Analysis

### Tracking Methods

**Manual Tracking**:
- Receipts and spending journals
- Monthly expense categorization
- Time-intensive but highly educational
- Increases spending awareness

**App-Based Tracking**:
- Mint, Personal Capital, PocketGuard
- Automatic transaction categorization
- Real-time spending alerts
- Trend analysis and reporting

**Bank and Credit Card Tools**:
- Built-in spending categorization
- Monthly and annual spending summaries
- Merchant-based analysis
- Integration with budgeting apps

### Expense Analysis Techniques

**Fixed vs. Variable Analysis**:
- Identify which expenses are controllable
- Focus optimization efforts on variable costs
- Look for fixed cost reduction opportunities
- Understand expense flexibility

**Needs vs. Wants Classification**:
- Distinguish between essential and discretionary spending
- Question each expense category's necessity
- Find substitutes for expensive wants
- Prioritize needs in tight budget situations

**Cost Per Use Analysis**:
- Calculate cost per use for purchases
- Evaluate quality vs. price trade-offs
- Consider long-term value of purchases
- Make more informed buying decisions

## Building Healthy Financial Habits

### Habit Formation Strategies

**Start Small and Build**:
- Begin with small, manageable changes
- Focus on one habit at a time
- Gradually increase difficulty or scope
- Celebrate small wins to build momentum

**Automation and Systems**:
- Automate savings and bill payments
- Set up systematic investment plans
- Use technology to reduce decision fatigue
- Create systems that work without constant attention

**Environmental Design**:
- Remove temptations from spending environment
- Make saving easier than spending
- Use visual cues for financial goals
- Create supportive social environment

### Common Financial Habits to Develop

**Daily Habits**:
- Track expenses as they occur
- Review account balances
- Avoid impulse purchases
- Practice gratitude for what you have

**Weekly Habits**:
- Review weekly spending against budget
- Plan upcoming week's expenses
- Look for deals and discounts
- Assess progress toward financial goals

**Monthly Habits**:
- Complete monthly budget review
- Pay all bills on time
- Review and rebalance investment accounts
- Assess and adjust financial goals

**Annual Habits**:
- Complete comprehensive financial review
- Update insurance coverage
- Review and adjust tax strategies
- Estate planning review and updates

## Emergency Fund Planning

### Emergency Fund Basics

**Purpose and Importance**:
- Covers unexpected expenses without debt
- Provides financial security and peace of mind
- Prevents derailing of long-term financial goals
- Reduces stress during difficult times

**Target Amount Guidelines**:
- **Minimum**: $1,000 starter emergency fund
- **Standard**: 3-6 months of living expenses
- **Conservative**: 6-12 months for irregular income
- **High-Risk Situations**: 12+ months for job uncertainty

### Building Your Emergency Fund

**Funding Strategies**:
- Start with small, consistent contributions
- Use windfalls (tax refunds, bonuses) to boost fund
- Sell unused items to jumpstart savings
- Take on temporary side work to accelerate building

**Where to Keep Emergency Funds**:
- **High-Yield Savings Account**: Easy access, some growth
- **Money Market Account**: Higher rates, limited transactions
- **Short-term CDs**: Slightly higher rates, less liquidity
- **Combination Approach**: Split between accounts for optimization

**Emergency Fund Priorities**:
1. Build starter fund ($1,000) quickly
2. Pay off high-interest debt
3. Build full emergency fund (3-6 months expenses)
4. Begin investing for long-term goals

## Debt Management Strategies

### Debt Assessment and Prioritization

**Debt Inventory**:
- List all debts with balances, interest rates, minimum payments
- Calculate total debt burden
- Identify highest-cost debt
- Understand payment terms and options

**Debt Avalanche Method**:
- Pay minimums on all debts
- Put extra payments toward highest interest rate debt
- Mathematically optimal approach
- Saves most money in interest over time

**Debt Snowball Method**:
- Pay minimums on all debts
- Put extra payments toward smallest balance
- Provides psychological wins and motivation
- May cost more in interest but builds momentum

### Credit Management

**Credit Score Factors**:
- **Payment History (35%)**: On-time payments most important
- **Credit Utilization (30%)**: Keep balances low relative to limits
- **Length of Credit History (15%)**: Longer history better
- **Credit Mix (10%)**: Variety of account types
- **New Credit (10%)**: Limit hard inquiries and new accounts

**Credit Score Improvement**:
- Pay all bills on time, every time
- Keep credit utilization below 30% (ideally under 10%)
- Don't close old credit cards
- Monitor credit reports for errors
- Consider becoming authorized user on family member's account

### Advanced Debt Strategies

**Balance Transfers**:
- Move high-interest debt to lower-rate cards
- Take advantage of promotional 0% APR offers
- Factor in transfer fees and timeline
- Have payoff plan before promotional rate ends

**Debt Consolidation**:
- Combine multiple debts into single payment
- Potentially lower interest rate
- Simplifies debt management
- Consider personal loans or home equity options

**Negotiation and Settlement**:
- Contact creditors to negotiate payment terms
- Request interest rate reductions
- Consider settlement for severely delinquent accounts
- Understand credit score implications

Developing strong cash flow management and financial habits creates the foundation for long-term financial success and wealth building.`
        }
      ],
      quiz: {
        id: "budgeting-module-quiz",
        title: "Budgeting and Cash Flow Management Quiz",
        description: "Test your knowledge of budgeting fundamentals",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What should be the first priority when building an emergency fund?",
            options: [
              "Save 6 months of expenses immediately",
              "Invest in stocks for growth",
              "Build a starter fund of $1,000",
              "Pay off all debt first"
            ],
            correctAnswer: 2,
            explanation: "Building a starter emergency fund of $1,000 provides immediate protection against small emergencies while you work on other financial goals."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which debt payoff method focuses on the highest interest rate debt first?",
            options: [
              "Debt Snowball",
              "Debt Avalanche",
              "Balance Transfer",
              "Debt Consolidation"
            ],
            correctAnswer: 1,
            explanation: "The Debt Avalanche method prioritizes paying off the highest interest rate debt first to minimize total interest paid."
          }
        ]
      }
    },
    {
      id: "investing-fundamentals",
      title: "Investment Fundamentals",
      description: "Stock, bond, and mutual fund investing for beginners",
      order: 2,
      estimatedHours: 4.0,
      content: [
        {
          id: "investment-basics",
          title: "Investment Basics for Beginners",
          description: "Understanding stocks, bonds, mutual funds, and ETFs",
          type: "video",
          duration: 35
        },
        {
          id: "investment-vehicles-comprehensive",
          title: "Investment Vehicles and Portfolio Building",
          description: "Comprehensive guide to investment options and portfolio construction",
          type: "text",
          duration: 55,
          textContent: `# Investment Vehicles and Portfolio Building

## Investment Fundamentals

### Risk and Return Relationship

**Risk-Return Trade-off**
- Higher potential returns generally require accepting higher risk
- Low-risk investments typically offer lower returns
- Diversification can help optimize risk-return balance
- Time horizon affects appropriate risk tolerance

**Types of Investment Risk**
- **Market Risk**: Overall market movements affecting all investments
- **Credit Risk**: Risk of issuer defaulting on obligations
- **Inflation Risk**: Purchasing power erosion over time
- **Liquidity Risk**: Difficulty selling investment quickly
- **Interest Rate Risk**: Impact of changing interest rates
- **Currency Risk**: Foreign exchange rate fluctuations

### Time Value of Money

**Compound Interest Power**
Future Value = Present Value × (1 + interest rate)^time
- Small differences in returns compound significantly over time
- Starting early dramatically increases final wealth
- Regular contributions amplify compounding effect

**Rule of 72**
Time to Double = 72 ÷ Annual Return Rate
- 6% return: 12 years to double
- 8% return: 9 years to double
- 10% return: 7.2 years to double

## Stock Market Investing

### Understanding Stocks

**What Stocks Represent**
- Ownership shares in publicly traded companies
- Claim on company's assets and earnings
- Voting rights in corporate decisions
- Potential for capital appreciation and dividends

**Types of Stocks**
- **Growth Stocks**: Companies expected to grow faster than market
- **Value Stocks**: Companies trading below intrinsic value
- **Dividend Stocks**: Companies paying regular dividends
- **Blue-Chip Stocks**: Large, established, financially stable companies
- **Small-Cap, Mid-Cap, Large-Cap**: Based on market capitalization

**Stock Analysis Basics**
- **Fundamental Analysis**: Company financials, management, industry trends
- **Technical Analysis**: Price patterns and market sentiment
- **Key Metrics**: P/E ratio, dividend yield, revenue growth, profit margins

### Stock Investment Strategies

**Buy and Hold**
- Long-term investment approach
- Minimizes transaction costs and taxes
- Requires patience and discipline
- Benefits from long-term market growth

**Dollar-Cost Averaging**
- Investing fixed amount regularly regardless of price
- Reduces impact of market volatility
- Builds discipline and consistency
- Good strategy for beginners

**Dividend Growth Investing**
- Focus on companies with growing dividend payments
- Provides income and inflation protection
- Typically more conservative approach
- Suitable for income-focused investors

## Bond Investing

### Bond Fundamentals

**How Bonds Work**
- Loans to governments or corporations
- Fixed interest payments (coupons)
- Principal repayment at maturity
- Generally less risky than stocks

**Types of Bonds**
- **Government Bonds**: Treasury bills, notes, bonds; municipal bonds
- **Corporate Bonds**: Investment grade and high-yield (junk) bonds
- **International Bonds**: Foreign government and corporate bonds
- **Inflation-Protected Bonds**: TIPS adjust principal for inflation

**Bond Risk Factors**
- **Interest Rate Risk**: Bond prices move opposite to interest rates
- **Credit Risk**: Risk of issuer default
- **Inflation Risk**: Fixed payments lose purchasing power
- **Call Risk**: Issuer may redeem bonds early

### Bond Investment Strategies

**Bond Laddering**
- Purchase bonds with staggered maturity dates
- Provides regular income and reinvestment opportunities
- Reduces interest rate risk
- Maintains liquidity

**Duration Matching**
- Match bond duration to investment timeline
- Shorter duration for near-term needs
- Longer duration for higher yields (if appropriate)
- Consider interest rate environment

## Mutual Funds and ETFs

### Mutual Fund Basics

**Structure and Benefits**
- Pooled investment from many investors
- Professional management
- Instant diversification
- Lower minimum investments
- Automatic reinvestment options

**Types of Mutual Funds**
- **Stock Funds**: Large-cap, small-cap, international, sector-specific
- **Bond Funds**: Government, corporate, municipal, international
- **Balanced Funds**: Mix of stocks and bonds
- **Target-Date Funds**: Age-appropriate asset allocation
- **Index Funds**: Passive tracking of market indices

**Active vs. Passive Management**
- **Active**: Fund manager tries to beat market
- **Passive**: Tracks index with minimal management
- **Cost Differences**: Active funds typically have higher fees
- **Performance**: Most active funds underperform indices long-term

### Exchange-Traded Funds (ETFs)

**ETF Advantages**
- Trade like stocks during market hours
- Generally lower expense ratios than mutual funds
- Tax efficiency through in-kind redemptions
- Transparency of holdings
- No minimum investment requirements

**Types of ETFs**
- **Broad Market ETFs**: Total stock market, S&P 500
- **Sector ETFs**: Technology, healthcare, energy
- **International ETFs**: Developed and emerging markets
- **Bond ETFs**: Various bond categories
- **Commodity ETFs**: Gold, oil, agricultural products
- **Factor ETFs**: Value, growth, momentum, quality

## Portfolio Construction

### Asset Allocation Principles

**Strategic Asset Allocation**
- Long-term mix of asset classes
- Based on goals, timeline, risk tolerance
- Rebalanced periodically to maintain targets
- Foundation of portfolio strategy

**Age-Based Allocation Rules**
- **100 minus age**: Percentage in stocks
- **120 minus age**: Modified for longer lifespans
- **Target-date approach**: Automatically adjusts over time
- **Personal factors**: May require customization

### Diversification Strategies

**Asset Class Diversification**
- Stocks, bonds, real estate, commodities
- Domestic and international exposure
- Various market capitalizations
- Different sectors and industries

**Geographic Diversification**
- **Domestic**: US market exposure
- **Developed International**: Europe, Japan, Australia
- **Emerging Markets**: Higher growth potential, higher risk
- **Currency Considerations**: Impact of exchange rates

**Rebalancing**
- **Calendar Rebalancing**: Set schedule (quarterly, annually)
- **Threshold Rebalancing**: When allocation drifts beyond limits
- **Benefits**: Forces buying low and selling high
- **Tax Considerations**: Use tax-advantaged accounts when possible

## Investment Accounts

### Taxable Investment Accounts

**Brokerage Accounts**
- No contribution limits
- Full liquidity and flexibility
- Taxed on dividends, interest, and capital gains
- Good for short to medium-term goals

**Tax Efficiency Strategies**
- **Tax-Loss Harvesting**: Realize losses to offset gains
- **Asset Location**: Hold tax-inefficient investments in tax-advantaged accounts
- **Index Funds**: Generally more tax-efficient than active funds
- **Hold Period**: Long-term capital gains rates lower than short-term

### Tax-Advantaged Accounts

**401(k) and Similar Employer Plans**
- **Traditional**: Tax deduction now, taxed in retirement
- **Roth**: After-tax contributions, tax-free growth and withdrawals
- **Employer Match**: Free money, prioritize getting full match
- **Contribution Limits**: $23,000 in 2024, higher for 50+

**Individual Retirement Accounts (IRAs)**
- **Traditional IRA**: Tax deduction subject to income limits
- **Roth IRA**: After-tax contributions, income limits apply
- **Conversion Strategies**: Converting traditional to Roth
- **Required Distributions**: Traditional IRAs require distributions at 73

## Investment Psychology and Behavior

### Common Investment Mistakes

**Emotional Decision Making**
- **Fear**: Selling during market downturns
- **Greed**: Chasing hot investments
- **Overconfidence**: Trading too frequently
- **Loss Aversion**: Holding losing investments too long

**Timing and Selection Mistakes**
- **Market Timing**: Trying to predict market movements
- **Stock Picking**: Concentrating in individual stocks
- **Chasing Performance**: Buying last year's winners
- **Neglecting Costs**: Ignoring fees and expenses

### Successful Investment Behaviors

**Discipline and Patience**
- Stick to long-term plan through market volatility
- Regular investing regardless of market conditions
- Focus on time in market, not timing the market
- Automate investments to reduce emotional decisions

**Continuous Learning**
- Stay informed about investment principles
- Understand what you invest in
- Learn from mistakes without major course changes
- Seek education, not hot tips

## Getting Started with Investing

### Investment Readiness Checklist

**Prerequisites**
1. Emergency fund established (3-6 months expenses)
2. High-interest debt paid off
3. Stable income and budget
4. Clear investment goals and timeline

**Account Setup Process**
1. Choose reputable broker or investment company
2. Compare fees, investment options, and services
3. Open appropriate account types
4. Set up automatic investing
5. Start with simple, diversified investments

### First Investment Recommendations

**Simple Starting Portfolio**
- **Target-Date Fund**: Age-appropriate, automatically managed
- **Three-Fund Portfolio**: Total stock market, international stocks, bonds
- **Robo-Advisor**: Automated, low-cost portfolio management
- **Index Funds**: Low-cost, broad market exposure

**Investment Amount Guidelines**
- Start with what you can afford consistently
- Increase contributions with income growth
- Aim for 10-15% of income for retirement
- Additional savings for other goals

Building a successful investment portfolio requires understanding basic principles, choosing appropriate investments, and maintaining discipline through market cycles while continuously learning and adapting strategies.`
        },
        {
          id: "portfolio-builder",
          title: "Personal Investment Portfolio Builder",
          description: "Create a diversified investment portfolio based on your goals",
          type: "finance-interactive",
          duration: 45,
          interactiveData: {
            exerciseType: "portfolio-construction",
            instructions: "Build a personalized investment portfolio by selecting appropriate asset allocation based on your age, risk tolerance, and investment timeline.",
            scenario: {
              investor: {
                age: 30,
                riskTolerance: "moderate",
                investmentHorizon: 35,
                goals: ["Retirement", "House Down Payment", "Children's Education"]
              },
              investmentOptions: [
                { name: "US Total Stock Market", category: "stocks", risk: "medium", expectedReturn: 0.08 },
                { name: "International Stocks", category: "stocks", risk: "medium-high", expectedReturn: 0.075 },
                { name: "Bonds", category: "bonds", risk: "low", expectedReturn: 0.04 },
                { name: "REITs", category: "real-estate", risk: "medium", expectedReturn: 0.06 },
                { name: "Cash/Money Market", category: "cash", risk: "very-low", expectedReturn: 0.02 }
              ]
            },
            hints: [
              "Consider your age for stock/bond allocation",
              "Diversify across asset classes and geographies",
              "Match risk level to your comfort and timeline",
              "Start simple and add complexity over time"
            ]
          }
        }
      ],
      quiz: {
        id: "investing-fundamentals-quiz",
        title: "Investment Fundamentals Quiz",
        description: "Test your knowledge of investment basics",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the primary benefit of diversification?",
            options: [
              "Guarantees higher returns",
              "Eliminates all investment risk",
              "Reduces portfolio risk without sacrificing expected return",
              "Provides immediate liquidity"
            ],
            correctAnswer: 2,
            explanation: "Diversification helps reduce portfolio risk by spreading investments across different assets that don't move in perfect correlation."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is dollar-cost averaging?",
            options: [
              "Investing a lump sum all at once",
              "Investing fixed amounts at regular intervals",
              "Buying only when prices are low",
              "Selling investments gradually"
            ],
            correctAnswer: 1,
            explanation: "Dollar-cost averaging involves investing a fixed amount regularly, regardless of market conditions, which can reduce the impact of volatility."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "Which type of investment account offers tax-free growth and withdrawals in retirement?",
            options: [
              "Traditional 401(k)",
              "Roth IRA",
              "Taxable brokerage account",
              "Traditional IRA"
            ],
            correctAnswer: 1,
            explanation: "Roth IRAs offer tax-free growth and tax-free withdrawals in retirement, funded with after-tax contributions."
          }
        ]
      }
    },
    {
      id: "retirement-planning",
      title: "Retirement Planning",
      description: "401(k)s, IRAs, and retirement savings strategies",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "retirement-accounts",
          title: "Retirement Account Options",
          description: "Understanding 401(k), IRA, and other retirement vehicles",
          type: "video",
          duration: 40
        }
      ]
    },
    {
      id: "tax-planning",
      title: "Tax Planning Strategies",
      description: "Tax-efficient investing and deduction strategies",
      order: 4,
      estimatedHours: 2.5,
      content: [
        {
          id: "tax-strategies",
          title: "Personal Tax Strategies",
          description: "Minimizing taxes through planning and optimization",
          type: "video",
          duration: 30
        }
      ]
    },
    {
      id: "insurance-estate",
      title: "Insurance and Estate Planning",
      description: "Life insurance, disability insurance, and basic estate planning",
      order: 5,
      estimatedHours: 2.5,
      content: [
        {
          id: "insurance-basics",
          title: "Insurance Essentials",
          description: "Life, disability, and property insurance needs",
          type: "video",
          duration: 25
        }
      ]
    }
  ],

  assignments: [
    {
      id: "personal-financial-plan",
      title: "Complete Personal Financial Plan",
      description: "Develop comprehensive personal financial plan including budget, investment strategy, and goal timeline",
      type: "project",
      dueWeeks: 4,
      maxScore: 100,
      instructions: "Create detailed personal financial plan with specific goals and implementation timeline.",
      submissionFormat: "pdf",
      resources: [
        "Financial planning templates",
        "Investment calculators",
        "Tax planning guides"
      ]
    }
  ],

  finalExam: {
    id: "personal-finance-final",
    title: "Personal Finance Mastery Final Exam",
    description: "Comprehensive examination of personal finance concepts",
    timeLimit: 90,
    passingScore: 75,
    attempts: 3,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "What is the recommended size for an emergency fund?",
        options: [
          "1-2 months of expenses",
          "3-6 months of expenses",
          "6-12 months of expenses",
          "1-2 years of expenses"
        ],
        correctAnswer: 1,
        explanation: "Financial experts typically recommend an emergency fund covering 3-6 months of living expenses to handle unexpected financial challenges."
      }
    ]
  },

  quizzes: []
};